import React, { useRef, useState, useEffect } from 'react'
import { OverlayTrigger, Popover, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons'
import { useAnalyticMutations } from './useAnalyticMutations'
import AnalyticFlagChoice from './AnalyticFlagChoice'
import renderDescription from 'shared/render-description'

function AnalyticFilterableHeader({
  analytic,
  dashboardId,
  params,
  setParams,
  popoverId = '',
  activePopoverId = null,
  setActivePopoverId = null,
  canEdit = true
}) {
  const showPopover = activePopoverId === popoverId
  const target = useRef(null)

  // Find the current analytic in params to get its state
  const currentAnalytic = params.analytics?.find((a) => a.id === analytic.id)
  const isEnabled = !!currentAnalytic
  const currentOption =
    currentAnalytic?.options || analytic.options || 'include'

  const [localOption, setLocalOption] = useState(currentOption)

  // Update local option when the analytic option changes
  useEffect(() => {
    setLocalOption(currentOption)
  }, [currentOption])

  const handleTogglePopover = (show) => {
    if (setActivePopoverId) {
      setActivePopoverId(show ? popoverId : null)
    }
  }

  const { createAnalytic, updateAnalytic, deleteAnalytic, isPending } =
    useAnalyticMutations({
      dashboardId,
      analyticId: analytic.id,
      currentAnalytic
    })

  const handleToggleEnabled = async (enabled, option = localOption) => {
    if (!canEdit || isPending) return

    if (enabled) {
      const res = await createAnalytic({
        dashboard_id: dashboardId,
        analytic_id: analytic.id,
        options: option
      })
      setParams((prev) => ({
        ...prev,
        analytics: [
          ...prev.analytics.filter((a) => a.id !== analytic.id),
          { id: analytic.id, options: option, dashboard_analytic_id: res.id }
        ]
      }))
    } else {
      if (currentAnalytic?.dashboard_analytic_id) {
        await deleteAnalytic(currentAnalytic.dashboard_analytic_id)
      }
      setParams((prev) => ({
        ...prev,
        analytics: prev.analytics.filter((a) => a.id !== analytic.id)
      }))
    }
  }

  const handleOptionChange = async (option) => {
    if (!canEdit || isPending) return

    setLocalOption(option)

    if (currentAnalytic?.dashboard_analytic_id) {
      await updateAnalytic({
        id: currentAnalytic.dashboard_analytic_id,
        options: option
      })
    }

    setParams((prev) => ({
      ...prev,
      page: 1,
      analytics: prev.analytics.map((a) =>
        a.id === analytic.id ? { ...a, options: option } : a
      )
    }))
  }

  const handleClear = async () => {
    if (!canEdit || isPending) return

    if (currentAnalytic?.dashboard_analytic_id) {
      await deleteAnalytic(currentAnalytic.dashboard_analytic_id)
    }
    setParams((prev) => ({
      ...prev,
      analytics: prev.analytics.filter((a) => a.id !== analytic.id)
    }))
    handleTogglePopover(false)
  }

  const hasActiveFilter = isEnabled

  const popover = (
    <Popover
      id={`popover-analytic-${analytic.id}`}
      className="filterable-header-popover"
    >
      <Popover.Body>
        <div>
          <div className="mb-3">
            <strong>{analytic.name}</strong>

            <div className="small text-muted">{renderDescription(analytic.description, analytic.column_sources)}</div>
          </div>

          <hr />

          <AnalyticFlagChoice
            value={isEnabled ? localOption : null}
            onChange={(option) => {
              if (!option) {
                handleToggleEnabled(false)
              } else if (!isEnabled) {
                setLocalOption(option)
                handleToggleEnabled(true, option)
              } else {
                handleOptionChange(option)
              }
            }}
            disabled={!canEdit}
            isPending={isPending}
          />

          <div className="mt-4 d-flex justify-content-between gap-2">
            <Button
              variant="light"
              size="sm"
              onClick={handleClear}
              style={{ visibility: isEnabled ? 'visible' : 'hidden' }}
              disabled={!canEdit || isPending}
            >
              Clear
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleTogglePopover(false)}
            >
              {canEdit ? 'Apply' : 'Close'}
            </Button>
          </div>
        </div>
      </Popover.Body>
    </Popover>
  )

  return (
    <th className="text-center" style={{ cursor: 'default' }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        {analytic.name.length > 50
          ? analytic.name.substring(0, 47) + '...'
          : analytic.name}
        <OverlayTrigger
          trigger="click"
          placement="bottom"
          overlay={popover}
          show={showPopover}
          onToggle={(nextShow) => {
            if (!nextShow) {
              handleTogglePopover(false)
            }
          }}
          rootClose
        >
          <Button
            ref={target}
            variant={hasActiveFilter ? 'primary' : 'light'}
            size="sm"
            className="ms-1 border-0"
            style={{
              padding: '2px 6px',
              minWidth: 'auto'
            }}
            onClick={(e) => {
              e.stopPropagation()
              handleTogglePopover(!showPopover)
            }}
            title={!canEdit ? 'View only - only the creator or permitted users can edit filters' : ''}
          >
            <FontAwesomeIcon icon={faFilter} size="xs" />
          </Button>
        </OverlayTrigger>
      </span>
    </th>
  )
}

export default AnalyticFilterableHeader
